import { useState, useMemo, useEffect } from 'react'
import { useApp } from '@/context/AppContext'
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine, Label } from 'recharts'
import { getTribeColor } from '@/utils/colors'
import { buildFilterKey } from '@/services/filterEngine'
import type { TribeId } from '@/types'

// Pair of questions for comparison with labels
interface QuestionPair {
  id: string
  label: string
  xQuestion: string
  xLabel: string
  yQuestion: string
  yLabel: string
}

// Pre-defined question pairs for comparison
const QUESTION_PAIRS: QuestionPair[] = [
  {
    id: 'parks-support',
    label: 'Park Support vs IPA Support',
    xQuestion: 'Support_NewParks',
    xLabel: 'Support for New Parks',
    yQuestion: 'Support_IPA_Communities',
    yLabel: 'Support for Indigenous-led IPAs',
  },
  {
    id: 'nature-values',
    label: 'Nature Access vs Nature Protection',
    xQuestion: 'Agree_NatureGoodHealth',
    xLabel: 'Nature Good for Health',
    yQuestion: 'Agree_MoreProtectedAreas',
    yLabel: 'Need More Protected Areas',
  },
  {
    id: 'conservation-extraction',
    label: 'Conservation vs Extraction',
    xQuestion: 'Support_NewParks',
    xLabel: 'Support New Parks',
    yQuestion: 'Agree_IndustryDestroyingEnv',
    yLabel: 'Industry Destroying Environment',
  },
  {
    id: 'traditional-progressive',
    label: 'Traditional vs Progressive Values',
    xQuestion: 'Agree_TraditionalRoles',
    xLabel: 'Traditional Gender Roles',
    yQuestion: 'Agree_ImmigrantsStrengthen',
    yLabel: 'Immigrants Strengthen Canada',
  },
  {
    id: 'climate-concerns',
    label: 'Climate Belief vs Climate Concern',
    xQuestion: 'Agree_HumansCarbonClimate',
    xLabel: 'Humans Cause Climate Change',
    yQuestion: 'Threat_ClimateChange',
    yLabel: 'Climate Change as Threat',
  },
]

// Tribe IDs for coloring
const TRIBE_IDS: TribeId[] = [
  'pro-extraction-populists',
  'community-first-elders',
  'new-canadian-materialists',
  'pessimistic-eco-sympathizers',
  'indigenous-identified',
  'older-institutionalists',
  'mainstream-progressives',
  'the-woke',
]

// Tribe display names
const TRIBE_NAMES: Record<TribeId, string> = {
  'pro-extraction-populists': 'Pro-Extraction Populists',
  'community-first-elders': 'Community-First Elders',
  'new-canadian-materialists': 'New Canadian Materialists',
  'pessimistic-eco-sympathizers': 'Struggling Green Populists',
  'indigenous-identified': 'Indigenous Identified',
  'older-institutionalists': 'Older Institutionalists',
  'mainstream-progressives': 'Mainstream Progressives',
  'the-woke': 'The Woke',
  'total': 'Total Population',
}

// Data point type for scatterplot
interface DataPoint {
  id: string
  name: string
  x: number
  y: number
  size: number
  color: string
}

// Calculate a score from question responses (positive responses = higher score)
function calculateScore(responses: Array<{ label: string; percentage: number }>): number {
  const weights: Record<string, number> = {
    'Strongly support': 100, 'Strongly Agree': 100, 'A top threat': 100, 'Extremely Concerned': 100,
    'Support': 75, 'Somewhat Agree': 75, 'Major threat': 75, 'Very Concerned': 75,
    'Neutral / Unsure': 50, 'Neutral': 50, 'Unsure': 50, 'Minor threat': 50, 'Somewhat Concerned': 50,
    'Oppose': 25, 'Somewhat Disagree': 25, 'Not a threat': 25, 'Not Very Concerned': 25,
    'Strongly oppose': 0, 'Strongly Disagree': 0, 'Not at all Concerned': 0,
  }

  let totalWeight = 0
  let totalScore = 0

  for (const resp of responses) {
    const weight = weights[resp.label] ?? 50
    totalScore += weight * resp.percentage
    totalWeight += resp.percentage
  }

  return totalWeight > 0 ? totalScore / totalWeight : 50
}

// Custom tooltip component
function ScatterTooltipContent({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ payload: DataPoint }>
}) {
  if (!active || !payload || payload.length === 0) return null
  const data = payload[0].payload
  return (
    <div className="bg-white dark:bg-slate-800 shadow-lg rounded-lg p-3 border border-slate-200 dark:border-slate-700">
      <div className="font-medium text-slate-900 dark:text-white">{data.name}</div>
      <div className="text-sm text-slate-600 dark:text-slate-400">
        n={data.size.toLocaleString()}
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-500 mt-1">
        X: {data.x.toFixed(0)}% | Y: {data.y.toFixed(0)}%
      </div>
    </div>
  )
}

// Custom scatter label
function CustomScatterLabel({ x, y, name, fill }: { x?: number; y?: number; name?: string; fill?: string }) {
  if (x === undefined || y === undefined || !name) return null
  return (
    <text
      x={x + 12}
      y={y}
      fill={fill || '#1e293b'}
      fontSize={10}
      dominantBaseline="middle"
      style={{ pointerEvents: 'none' }}
    >
      {name}
    </text>
  )
}

// Calculate dynamic domain with padding
function calculateDomain(values: number[], padding = 0.15): [number, number] {
  if (values.length === 0) return [0, 100]
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min
  const paddedMin = Math.max(0, min - range * padding)
  const paddedMax = Math.min(100, max + range * padding)
  if (paddedMax - paddedMin < 20) {
    const center = (min + max) / 2
    return [Math.max(0, center - 15), Math.min(100, center + 15)]
  }
  return [paddedMin, paddedMax]
}

export function QuestionComparison() {
  const { state } = useApp()
  const { data } = state
  const [selectedPairId, setSelectedPairId] = useState<string>(QUESTION_PAIRS[0].id)
  const [isDarkMode, setIsDarkMode] = useState(false)

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'))
    }
    checkDarkMode()
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  // Dynamic colors for dark mode
  const labelColor = isDarkMode ? '#f1f5f9' : '#1e293b'
  const axisLabelColor = isDarkMode ? '#e2e8f0' : '#666'
  const referenceLineColor = isDarkMode ? '#cbd5e1' : '#e2e8f0'
  const tickColor = isDarkMode ? '#94a3b8' : '#666'

  const selectedPair = QUESTION_PAIRS.find(p => p.id === selectedPairId) || QUESTION_PAIRS[0]

  // Calculate data points for each tribe
  const chartData = useMemo(() => {
    if (!data) return []

    const points: DataPoint[] = []

    for (const tribeId of TRIBE_IDS) {
      const xQuestion = data.questions[selectedPair.xQuestion]
      const yQuestion = data.questions[selectedPair.yQuestion]

      if (!xQuestion || !yQuestion) continue

      // Get data for this tribe
      const filterKey = buildFilterKey(tribeId, 'total')
      const xData = xQuestion.data[filterKey]
      const yData = yQuestion.data[filterKey]

      if (!xData || !yData) continue

      const xScore = calculateScore(xData.responses)
      const yScore = calculateScore(yData.responses)

      points.push({
        id: tribeId,
        name: TRIBE_NAMES[tribeId],
        x: xScore,
        y: yScore,
        size: xData.sampleSize,
        color: getTribeColor(tribeId),
      })
    }

    return points
  }, [data, selectedPair])

  // Calculate domains
  const { xDomain, yDomain } = useMemo(() => {
    const xValues = chartData.map(d => d.x)
    const yValues = chartData.map(d => d.y)
    return {
      xDomain: calculateDomain(xValues),
      yDomain: calculateDomain(yValues),
    }
  }, [chartData])

  const xCenter = (xDomain[0] + xDomain[1]) / 2
  const yCenter = (yDomain[0] + yDomain[1]) / 2

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Question Comparison
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Compare responses across tribes
        </span>
      </div>

      {/* Question pair selector */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-4">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Select Question Pair to Compare:
        </label>
        <select
          value={selectedPairId}
          onChange={(e) => setSelectedPairId(e.target.value)}
          className="w-full md:w-auto px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        >
          {QUESTION_PAIRS.map((pair) => (
            <option key={pair.id} value={pair.id}>
              {pair.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <div className="h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 120, bottom: 60, left: 60 }}>
              <ReferenceLine x={xCenter} stroke={referenceLineColor} strokeDasharray="3 3" />
              <ReferenceLine y={yCenter} stroke={referenceLineColor} strokeDasharray="3 3" />

              <XAxis
                type="number"
                dataKey="x"
                domain={xDomain}
                tickFormatter={(value) => `${value.toFixed(0)}%`}
                tick={{ fontSize: 10, fill: tickColor }}
              >
                <Label
                  value={selectedPair.xLabel}
                  position="insideBottom"
                  offset={-5}
                  style={{ fill: axisLabelColor, fontSize: 12 }}
                />
              </XAxis>
              <YAxis
                type="number"
                dataKey="y"
                domain={yDomain}
                tickFormatter={(value) => `${value.toFixed(0)}%`}
                tick={{ fontSize: 10, fill: tickColor }}
                width={50}
              >
                <Label
                  value={selectedPair.yLabel}
                  angle={-90}
                  position="insideLeft"
                  offset={-5}
                  style={{ fill: axisLabelColor, fontSize: 12, textAnchor: 'middle' }}
                />
              </YAxis>
              <ZAxis
                type="number"
                dataKey="size"
                range={[200, 600]}
                name="Sample Size"
              />
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              <Tooltip content={(props: any) => <ScatterTooltipContent active={props.active} payload={props.payload} />} />

              <Scatter
                name="Thought Tribes"
                data={chartData}
                label={<CustomScatterLabel fill={labelColor} />}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={entry.id}
                    fill={entry.color}
                    opacity={0.85}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {chartData.map((point) => (
            <div
              key={point.id}
              className="flex items-center gap-2 p-2 rounded"
            >
              <div
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: point.color }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400 leading-tight" title={point.name}>
                {point.name}
              </span>
            </div>
          ))}
        </div>

        {/* Explanation */}
        <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
          <h3 className="font-medium text-slate-800 dark:text-white mb-2">
            How to Read This Chart
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            This scatterplot compares how different Thought Tribes respond to two related questions.
            The X-axis shows <strong>{selectedPair.xLabel}</strong> and the Y-axis shows{' '}
            <strong>{selectedPair.yLabel}</strong>. Higher values indicate stronger agreement or support.
            Bubble size indicates segment population. Tribes that cluster together share similar views
            on both questions, while spread indicates divergence.
          </p>
        </div>
      </div>
    </div>
  )
}
